'use client'

import { useState } from 'react'
import { useRouter } from 'next/navigation'
import type { CartItem, OrderFormData, HowYouKnow, HandoffMethod } from '@/lib/types'

interface OrderRequestFormProps {
  cartItems: CartItem[]
  total: number
}

export default function OrderRequestForm({ cartItems, total }: OrderRequestFormProps) {
  const router = useRouter()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [phoneNumber, setPhoneNumber] = useState('')
  const [howYouKnowOther, setHowYouKnowOther] = useState('')
  const [formData, setFormData] = useState<OrderFormData>({
    parentName: '',
    parentEmail: '',
    howYouKnow: 'friend_of_squish' as HowYouKnow,
    handoffMethod: 'local_pickup' as HandoffMethod,
    paymentMethod: 'venmo',
    notes: '',
    agreedToTerms: false,
  })

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (!formData.agreedToTerms) {
      setError('You must agree to the terms to continue')
      return
    }

    setIsSubmitting(true)

    try {
      const response = await fetch('/api/orders/create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          formData,
          cartItems,
          total,
        }),
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.error || 'Failed to submit order')
      }

      // Clear cart and redirect
      localStorage.removeItem('squish-prints-cart')
      router.push(`/order-confirmation/${data.orderNumber}`)
    } catch (err: any) {
      setError(err.message || 'Something went wrong. Please try again.')
      setIsSubmitting(false)
    }
  }

  return (
    <div className="bg-white rounded-3xl shadow-soft p-6 sticky top-24">
      <div className="mb-6">
        <h2 className="text-2xl font-bold">Request Order 🎨</h2>
        <p className="text-sm text-gray-600 mt-1">
          Fill out the form below and we'll follow up by email.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Name */}
        <div>
          <label className="block text-sm font-medium mb-1">
            Name<span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            required
            value={formData.parentName}
            onChange={(e) => setFormData({ ...formData, parentName: e.target.value })}
            className="w-full border border-gray-300 rounded-2xl px-3 py-2 focus:ring-2 focus:ring-primary focus:border-transparent"
          />
        </div>

        {/* Email Address */}
        <div>
          <label className="block text-sm font-medium mb-1">
            Email Address<span className="text-red-500">*</span>
          </label>
          <input
            type="email"
            required
            value={formData.parentEmail}
            onChange={(e) => setFormData({ ...formData, parentEmail: e.target.value })}
            className="w-full border border-gray-300 rounded-2xl px-3 py-2 focus:ring-2 focus:ring-primary focus:border-transparent"
          />
        </div>

        {/* Phone Number (optional) */}
        <div>
          <label className="block text-sm font-medium mb-1">
            Phone Number (optional)
          </label>
          <input
            type="tel"
            value={phoneNumber}
            onChange={(e) => setPhoneNumber(e.target.value)}
            className="w-full border border-gray-300 rounded-2xl px-3 py-2 focus:ring-2 focus:ring-primary focus:border-transparent"
          />
        </div>

        {/* How did you find Squish Prints 3D? */}
        <div>
          <label className="block text-sm font-medium mb-1">
            How did you find Squish Prints 3D?<span className="text-red-500">*</span>
          </label>
          <select
            required
            value={formData.howYouKnow}
            onChange={(e) => setFormData({ ...formData, howYouKnow: e.target.value as HowYouKnow })}
            className="w-full border border-gray-300 rounded-2xl px-3 py-2 focus:ring-2 focus:ring-primary focus:border-transparent"
          >
            <option value="friend_of_squish">Friend of Squish</option>
            <option value="convention_festival">Convention / Festival</option>
            <option value="social_media">Social Media</option>
            <option value="family">Family</option>
            <option value="other">Other</option>
          </select>
          {formData.howYouKnow === 'other' && (
            <div className="mt-2">
              <input
                type="text"
                placeholder="Tell us how you found Squish Prints 3D"
                value={howYouKnowOther}
                onChange={(e) => setHowYouKnowOther(e.target.value)}
                className="w-full border border-gray-300 rounded-2xl px-3 py-2 focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>
          )}
        </div>

        {/* How will you receive your order? */}
        <div>
          <label className="block text-sm font-medium mb-1">
            How will you receive your order?<span className="text-red-500">*</span>
          </label>
          <select
            required
            value={formData.handoffMethod}
            onChange={(e) => setFormData({ ...formData, handoffMethod: e.target.value as HandoffMethod })}
            className="w-full border border-gray-300 rounded-2xl px-3 py-2 focus:ring-2 focus:ring-primary focus:border-transparent"
          >
            <option value="local_pickup">Local pickup</option>
            <option value="get_from_squish_school">Get from Squish at school</option>
            <option value="shipped">Shipped (USPS / UPS – customer pays shipping)</option>
            <option value="other_arrangement">Other arrangement</option>
          </select>
          <p className="text-xs text-gray-500 mt-1">
            Orders are printed after payment is received. We'll email you when your order is ready.
          </p>
        </div>

        {/* Comments */}
        <div>
          <label className="block text-sm font-medium mb-1">
            Comments (optional, max 200 characters)
          </label>
          <textarea
            maxLength={200}
            value={formData.notes}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            className="w-full border border-gray-300 rounded-2xl px-3 py-2 focus:ring-2 focus:ring-primary focus:border-transparent"
            rows={3}
          />
          <p className="text-xs text-gray-500 mt-1">{formData.notes.length}/200</p>
        </div>

        {/* Terms Checkbox */}
        <div className="flex items-start bg-cloud rounded-2xl p-3">
          <input
            type="checkbox"
            id="terms"
            checked={formData.agreedToTerms}
            onChange={(e) => setFormData({ ...formData, agreedToTerms: e.target.checked })}
            className="mt-1 mr-2"
          />
          <label htmlFor="terms" className="text-sm text-gray-700">
            I understand that my order will be printed after payment is received and I will be contacted by email when it's ready.<span className="text-red-500">*</span>
          </label>
        </div>

        {/* Payment Information */}
        <div className="bg-cloud rounded-2xl p-4">
          <p className="text-sm font-semibold mb-2">Payment</p>
          <p className="text-sm text-gray-700">
            Squish Prints is a small kid-run 3D printing shop.<br />
            Orders are printed after payment is received.
          </p>
          <p className="text-sm text-gray-700 mt-2">Payment options:</p>
          <ul className="text-sm text-gray-700 mt-1">
            <li>• Venmo</li>
            <li>• Cash</li>
          </ul>
          <p className="text-sm text-gray-700 mt-2">Thank you for supporting a young maker! 🧡</p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
            {error}
          </div>
        )}

        {/* Submit Button */}
        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full bg-primary text-white py-3 rounded-full font-semibold shadow-soft hover:shadow-pop transition-all disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSubmitting ? 'Submitting...' : 'Request Order'}
        </button>
      </form>
    </div>
  )
}
